/**
 * selling/ItemCard.jsx
 * ─────────────────────────────────────────────────────────────────────────────
 * Single product card displayed in the seller's listing grid.
 *
 * Shows: product image/video thumbnail, title, price, location,
 * status badge (Active/Sold), like count, and a click handler to
 * open the edit modal.
 *
 * PROPS:
 *   product {object}   — ad object from the API
 *   onClick {function} — called when the card is clicked (opens EditAdModal)
 * ─────────────────────────────────────────────────────────────────────────────
 */

import React from 'react';
import styles from './SellingTab.module.css';
import { FALLBACK_IMG, handleImgError, isVideoUrl, extractUrl, StarRating } from './sellingUtils';

export // ── Item card (grid) ──────────────────────────────────────────────────────────
const ItemCard = ({ product, onClick }) => {
  let videoUrl = null;
  let imgSrc = FALLBACK_IMG;

  // images[] and videos[] entries may be objects {image, video, url...} or plain strings.
  // extractUrl() safely pulls the URL string out of either shape.
  const rawImg = product.img || product.image || null;

  if (isVideoUrl(rawImg)) {
    videoUrl = extractUrl(rawImg);
  } else if (product.images && Array.isArray(product.images) && product.images.length > 0) {
    const videoEntry = product.images.find(img => isVideoUrl(extractUrl(img)));
    if (videoEntry) {
      videoUrl = extractUrl(videoEntry);
    } else {
      imgSrc = extractUrl(product.images[0]) || extractUrl(rawImg) || FALLBACK_IMG;
    }
  } else if (product.videos && Array.isArray(product.videos) && product.videos.length > 0) {
    videoUrl = extractUrl(product.videos[0]);
  } else {
    imgSrc = extractUrl(rawImg) || FALLBACK_IMG;
  }

  const likeCount = product.like_count ?? product.likes_count ?? product.likes ?? 0;
  const rating = product.rating ?? product.average_rating ?? 0;
  const ratingCnt = product.rating_count ?? product.reviewCount ?? 0;
  const isSold = product.status === 'sold';
  const location = product.city || product.location;

  return (
    <div
      onClick={onClick}
      style={{
        background: '#fff', borderRadius: '12px', overflow: 'hidden',
        boxShadow: '0 1px 4px rgba(0,0,0,0.10)', cursor: 'pointer',
        transition: 'transform 0.15s, box-shadow 0.15s',
        display: 'flex', flexDirection: 'column', opacity: isSold ? 0.75 : 1,
      }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 18px rgba(0,0,0,0.13)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.10)'; }}
    >
      <div style={{
        position: 'relative',
        width: '100%',
        aspectRatio: '4 / 3',
        background: '#f3f4f6',
        overflow: 'hidden',
        flexShrink: 0,
      }}>
        {videoUrl ? (
          <video
            src={videoUrl}
            muted loop playsInline
            onMouseEnter={e => e.currentTarget.play()}
            onMouseLeave={e => { e.currentTarget.pause(); e.currentTarget.currentTime = 0; }}
            style={{
              width: '100%', height: '100%',
              objectFit: 'contain',
              display: 'block',
              background: '#000',
            }}
          />
        ) : (
          <img
            src={imgSrc}
            alt={product.name}
            onError={handleImgError}
            style={{
              width: '100%', height: '100%',
              objectFit: 'contain',
              display: 'block',
            }}
          />
        )}

        {videoUrl && (
          <div style={{ position: 'absolute', top: 8, left: 8, background: 'rgba(0,0,0,0.70)', color: '#fff', fontSize: '9px', fontWeight: 700, padding: '3px 6px', borderRadius: '5px', display: 'flex', alignItems: 'center', gap: '3px' }}>
            <svg width="8" height="8" viewBox="0 0 24 24" fill="white"><path d="M8 5v14l11-7z"/></svg>VIDEO
          </div>
        )}

        {isSold && (
          <div style={{ position: 'absolute', top: 8, left: 8, background: 'rgba(239,68,68,0.90)', color: '#fff', fontSize: '9px', fontWeight: 800, padding: '3px 8px', borderRadius: '5px' }}>SOLD</div>
        )}

        <div style={{ position: 'absolute', top: 8, right: 8, background: 'rgba(0,0,0,0.55)', color: '#fff', borderRadius: '99px', padding: '4px 8px', fontSize: '11px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="white"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
          {likeCount}
        </div>

        {videoUrl && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
            <div style={{ width: 38, height: 38, borderRadius: '50%', background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M8 5v14l11-7z"/></svg>
            </div>
          </div>
        )}
      </div>

      <div style={{ padding: '10px 12px 12px', flex: 1, display: 'flex', flexDirection: 'column' }}>
        <div style={{ fontSize: '13px', fontWeight: 600, color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginBottom: '4px' }}>
          {product.name}
        </div>
        <div style={{ fontSize: '15px', fontWeight: 700, color: '#16a34a', marginBottom: '4px' }}>
          KES {product.price?.toLocaleString() || '0'}
        </div>
        {location && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2.5">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
            </svg>
            {location}
          </div>
        )}
        <div style={{ marginTop: 'auto', paddingTop: '6px' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '20px', background: isSold ? 'rgba(239,68,68,0.10)' : 'rgba(22,163,74,0.10)', color: isSold ? '#dc2626' : '#16a34a' }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: isSold ? '#dc2626' : '#16a34a', display: 'inline-block' }} />
            {isSold ? 'Sold' : 'Active'}
          </span>
        </div>
        {rating > 0 && <StarRating rating={rating} count={ratingCnt} />}
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// VIEW ALL STATS MODAL
// ══════════════════════════════════════════════════════════════════════════════

export default ItemCard;
